import { createInstance, enums, setLogger, ReactSDKClient } from '@optimizely/react-sdk';
import { fetchWithCache } from 'app/lib/cache';
import http from 'app/lib/http';
import Settings from 'app/config/settings';
import * as Sentry from '@sentry/browser';
import { OptimizelyConfig, Features } from '@goodrx/heydoctor-core';
import { trackEvent } from 'app/lib/analytics';
import { CookieUniqueId } from 'app/config/constants';
import { cookies, CtxContext } from 'app/lib/storage';
import { Config, UserAttributes } from '@optimizely/optimizely-sdk';
import { isClient } from 'app/lib/utils';
import { Experiments } from 'app/types/optimizely';
import settings from 'app/config/settings';
import Tenancy from 'app/lib/tenancy';
import { configureReq } from 'app/lib/sentry';
import { ParsedUrlQuery } from 'querystring';

/**
 * List of services that under A/B test - need to disable next-js link for correct test initialization
 */
export const DISABLE_NEXTJS_LINK = ['birth-control', 'erectile-dysfunction'];

const OPTIMIZELY_DATA_FILE_CACHE_KEY = 'optimizelyDataFile';

export const OPTIMIZELY_AUTOMATION_TEST_DATAFILE = {
  version: '4',
  rollouts: [],
  typedAudiences: [],
  anonymizeIP: false,
  projectId: '12345',
  variables: [],
  featureFlags: [],
  experiments: [],
  audiences: [],
  groups: [],
  attributes: [],
  botFiltering: false,
  accountId: '12345',
  events: [],
  revision: '1',
};

export type OptimizelyDataFile = Config['datafile'];

export interface OptimizelyClientProps {
  dataFile?: OptimizelyDataFile;
  query?: ParsedUrlQuery;
  user?: {
    id: string;
    attributes: UserAttributes;
  };
}

if (process.env.STAGE === 'prod') {
  // Turn off logging on production
  setLogger(null);
}

/**
 * Create OptimizelyClient
 * @see https://docs.developers.optimizely.com/full-stack/docs/initialize-sdk-react
 */
export function createOptimizelyClient({ dataFile, user, query }: OptimizelyClientProps) {
  const config: Config = {
    datafile: dataFile,
    errorHandler: {
      handleError: (exception: Error) => {
        Sentry.captureException(exception);
      },
    },
  };

  // provide sdk key key only if we have no datafile loaded (client side)
  if (!dataFile) {
    if (cookies.get()?.isTesting) config.datafile = OPTIMIZELY_AUTOMATION_TEST_DATAFILE;
    else config.sdkKey = Settings.OptimizelySdkKey;
  }

  const optimizelyClient = createInstance(config);

  if (isClient()) {
    // required to track event via segment - https://segment.com/docs/connections/destinations/catalog/optimizely-web/#optimizely-full-stack-javascript-sdk
    // @ts-ignore
    window.optimizelyClientInstance = optimizelyClient;
    /**
     * on client, report Feature start
     * type definition can be found here https://docs.developers.optimizely.com/full-stack/docs/set-up-notification-listener-php
     */
    optimizelyClient.notificationCenter.addNotificationListener<{
      type: 'ab-test' | 'feature' | 'feature-test' | 'feature-variable';
      userId: string;
      attributes: Record<string, unknown>;
      decisionInfo: {
        experimentKey?: string;
        featureEnabled?: string;
        featureKey?: string;
        variationKey?: string;
      };
    }>(enums.NOTIFICATION_TYPES.DECISION, ({ type, decisionInfo }) => {
      if (type === 'feature' && decisionInfo.featureEnabled) {
        trackEvent('Feature Enabled', { name: decisionInfo.featureKey });
      }
    });

    // report experiment activation event
    optimizelyClient.notificationCenter.addNotificationListener<{
      userId: string;
      attributes: Record<string, unknown>;
      experiment: { key: string };
      variation: { key: string };
    }>(enums.NOTIFICATION_TYPES.ACTIVATE, ({ experiment, variation }) => {
      trackEvent('Experiment Started', {
        name: experiment.key,
        variant: variation.key,
      });
    });
  }

  const optimizelyUser = user || getOptimizelyUser();
  optimizelyClient.setUser(optimizelyUser);

  optimizelySetForcedVariation(optimizelyClient, query, optimizelyUser.id);

  return optimizelyClient;
}

/**
 * Programmatically put a user into a certain variation
 * /?force-opty-<experiment_key>=<variation_key>
 * @see https://docs.developers.optimizely.com/full-stack/docs/use-forced-bucketing
 */
const optimizelySetForcedVariation = (
  optimizelyClient: ReactSDKClient,
  queryPassed?: ParsedUrlQuery,
  userId?: string
) => {
  const query =
    queryPassed ||
    (isClient() && Object.fromEntries && Object.fromEntries(new URLSearchParams(window.location.search))); // parse url on client side

  if (!userId || !query) return;

  const setForcedVariation = () =>
    Object.keys(query)
      .filter((key) => key.startsWith('force-opty-'))
      .forEach((key) => {
        optimizelyClient.setForcedVariation(key.replace('force-opty-', ''), userId, query[key] as string);
      });

  setForcedVariation();
  // for client side render only, we need to wait for onReady event before setForcedVariation
  optimizelyClient.onReady().then(setForcedVariation);
};

/**
 * Get initial Optimizely props, save optimizelyId in cookie
 * @param ctx
 */
export async function getOptimizelyProps(ctx: CtxContext): Promise<OptimizelyClientProps> {
  let dataFile;

  if (ctx?.res) {
    if (cookies.get(ctx).isTesting) {
      dataFile = OPTIMIZELY_AUTOMATION_TEST_DATAFILE;
    } else {
      try {
        // load optimizely datafile
        dataFile = await fetchWithCache(OPTIMIZELY_DATA_FILE_CACHE_KEY, async () => {
          return http
            .get(`https://cdn.optimizely.com/datafiles/${Settings.OptimizelySdkKey}.json`, { timeout: 2000 }) // timeout 2s
            .then((response) => response.data);
        });
      } catch (e) {
        Sentry.captureException(e);
      }
    }
  }

  return {
    query: ctx?.query,
    user: getOptimizelyUser(ctx),
    dataFile,
  };
}

export function getOptimizelyUser(ctx?: CtxContext) {
  return {
    id: getUniqueIdFromCookies(ctx) || setUniqueIdToCookies(ctx),
    attributes: {
      environment: settings.Env,
      client: Tenancy.getTenant(),
    },
  };
}

/**
 * Get grx_unique_id from cookies if exist
 */
export const getUniqueIdFromCookies = (ctx?: CtxContext): string | undefined => {
  if (!ctx) {
    return cookies.get()[CookieUniqueId.GRX];
  } else {
    const uniqueId = cookies.get(ctx)[CookieUniqueId.GRX];
    if (uniqueId) {
      return uniqueId;
    }

    const responseHeaders = ctx?.res?.getHeader('set-cookie');
    //if we on server and already set grx_unique_id to response cookie, parse response cookie
    if (responseHeaders && typeof responseHeaders === 'object') {
      const fakeCtx = {
        req: {
          headers: {
            cookie: responseHeaders?.join(';'),
          },
        },
      };

      return cookies.get(fakeCtx as CtxContext)[CookieUniqueId.GRX];
    }
  }

  return;
};

/**
 * Set and return UniqueId
 * @param ctx
 */
export function setUniqueIdToCookies(ctx?: CtxContext) {
  if (process.env.STAGE === 'prod' && !cookies.get(ctx)[CookieUniqueId.GRX]) {
    ctx?.req && configureReq(ctx.req);
    Sentry.withScope((scope) => {
      scope.setExtra('headers', JSON.stringify(ctx?.req?.headers));
      Sentry.captureMessage(`Running on production, but no grx_unique_id cookie`);
    });
  }

  const newId = `u-${Math.random().toString(36).substring(3, 13)}`;

  cookies.set(CookieUniqueId.GRX as 'grx_unique_id', newId, ctx);

  return newId;
}

/**
 * Check expiration date, console error if expired
 * @param type
 * @param name
 */
export function checkExpirationDate(type: 'Experiments' | 'Features', name: Experiments | Features) {
  const expiration = OptimizelyConfig[type]?.[name]?.expiration;

  if (expiration && new Date(expiration) < new Date()) {
    /* eslint-disable no-console */
    console.error(`${type} ${name} expired, please remove related code`);
  }
}
